import React from 'react';
import styled from 'styled-components';
import { useEffect } from 'react';
import { DateRangePicker } from 'react-date-range';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import emailService from '../../services/api/emailStatus';
import {
  sa,
  Card,
  PillButton,
  SelectPill,
  SectionHeaderRow,
} from '../../components/superAdminUi';

// Email Logs filters on the new Super Admin surface: type / survey / status pickers plus a
// date-range trigger inside a white Card, with a primary Generate action. The underlying
// filter contract (0 / '' meaning "all") is preserved exactly.

const Filters = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin-top: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-family: ${sa.font.family};
  font-size: 12px;
  font-weight: ${sa.font.semibold};
  color: ${sa.color.textGray};
`;

const DateTrigger = styled.input`
  background: ${sa.color.white};
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.sm};
  padding: 8px 14px;
  min-width: 260px;
  height: 38px;
  font-family: ${sa.font.family};
  font-size: 13px;
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
  cursor: pointer;
`;

const PickerAnchor = styled.div`
  position: relative;
`;

const PickerPanel = styled.div`
  position: absolute;
  z-index: 9999;
  border: 1px solid ${sa.color.stroke};
`;

const Loading = styled.div`
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${sa.color.textGray};
`;

export default function EmailFilters({ doSearch }) {
  const [selectedEmailType, setSelectedEmailType] = React.useState(0);
  const [selectedSurvey, setSelectedSurvey] = React.useState(0);
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [selectedToDate, setSelectedToDate] = React.useState(null);
  const [selectedFromDate, setSelectedFromDate] = React.useState(null);
  const [surveys, setSurveys] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showDateRangePicker, setShowDateRangePicker] = React.useState(false);

  const emailTypeOptions = [
    { value: '', label: 'All Email Types' },
    { value: 1, label: 'Participant Invite' },
    { value: 2, label: 'Reminder' },
    { value: 3, label: 'Owner Email' },
    { value: 4, label: 'Comment Reply' },
    { value: 5, label: 'Survey Published' },
    { value: 6, label: 'Resend Invite' },
    { value: 7, label: 'Error In Survey' },
    { value: 100, label: 'Safespace Reminder' },
    { value: 101, label: 'Safespace Escalation' },
    { value: 102, label: 'Safespace Followup' },
    { value: 103, label: 'Safespace Message Not Seen' },
  ];

  const emailStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'sent', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'open', label: 'Opened' },
    // { value: 'click', label: 'Clicked' },
    { value: 'bounce', label: 'Bounced' },
    { value: 'processed', label: 'Processed' },
  ];

  const surveyOptions = [
    { value: '', label: 'All Surveys' },
    ...surveys.map((survey) => ({ value: survey.id, label: survey.name })),
  ];

  const handleSearch = () => {
    setShowDateRangePicker(false);
    doSearch({
      surveyId: selectedSurvey,
      emailType: selectedEmailType,
      status: selectedStatus,
      fromDate: selectedFromDate,
      toDate: selectedToDate,
      pageCount: null,
    });
  };
  useEffect(() => {
    setSelectedToDate(new Date());
    setSelectedFromDate(
      new Date(new Date().setDate(new Date().getDate() - 30))
    );
    doSearch({
      surveyId: 0,
      emailType: 0,
      status: '',
      fromDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      toDate: new Date(),
      pageCount: null,
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    emailService
      .getSurveys()
      .then((response) => {
        if (response) {
          setSurveys(response);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  if (loading) {
    return (
      <Card>
        <Loading>Loading Filters...</Loading>
      </Card>
    );
  }

  return (
    <Card>
      <SectionHeaderRow title='Email Logs Filters' />
      <Filters>
        <Field>
          <FieldLabel>Email Type</FieldLabel>
          <SelectPill
            value={selectedEmailType === 0 ? '' : selectedEmailType}
            onChange={(value) =>
              setSelectedEmailType(value === '' ? 0 : Number(value))
            }
            options={emailTypeOptions}
            ariaLabel='Email Type'
            testId='email-type-filter'
          />
        </Field>
        <Field style={{ maxWidth: 550, width: 550 }}>
          <FieldLabel>Survey</FieldLabel>
          <SelectPill
            value={selectedSurvey === 0 ? '' : selectedSurvey}
            onChange={(value) =>
              setSelectedSurvey(value === '' ? 0 : Number(value))
            }
            options={surveyOptions}
            ariaLabel='Survey'
            testId='survey-filter'
          />
        </Field>
        <Field>
          <FieldLabel>Status</FieldLabel>
          <SelectPill
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            options={emailStatusOptions}
            ariaLabel='Status'
            testId='status-filter'
          />
        </Field>
        <Field>
          <FieldLabel>Date Range</FieldLabel>
          <PickerAnchor>
            <DateTrigger
              type='text'
              value={`From Date: ${
                selectedFromDate
                  ? selectedFromDate.toLocaleDateString('en-GB')
                  : ''
              }  - To Date: ${
                selectedToDate ? selectedToDate.toLocaleDateString('en-GB') : ''
              }`}
              readOnly
              onClick={() => setShowDateRangePicker(!showDateRangePicker)}
            />
            {showDateRangePicker && (
              <ClickAwayListener
                onClickAway={() => setShowDateRangePicker(false)}
                size='small'
              >
                <PickerPanel>
                  <DateRangePicker
                    editableDateInputs={true}
                    ranges={[
                      {
                        startDate: selectedFromDate,
                        endDate: selectedToDate,
                        key: 'selection',
                      },
                    ]}
                    minDate={new Date('2025-07-01')}
                    maxDate={new Date()}
                    direction='horizontal'
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    endDatePlaceholder='Continuo'
                    rangeColors={['#f33e5b', '#3ecf8e', '#fed14c']}
                    onChange={(ranges) => {
                      setSelectedFromDate(ranges.selection.startDate);
                      setSelectedToDate(ranges.selection.endDate);
                    }}
                  />
                </PickerPanel>
              </ClickAwayListener>
            )}
          </PickerAnchor>
        </Field>        
      </Filters>
      <div style={{ marginTop: '8px', textAlign: 'right' }}>
        <PillButton type='button' $size='lg' onClick={handleSearch}>
          Generate
        </PillButton>
      </div>
    </Card>
  );
}
